import { router } from "@/router";
import { Storage } from "@/common/utils/storage";

type PUSH_NOTIFICATION_ACTION_TYPE = {
  CREDENTIAL_EXPIRATION_REMINDER: {
    id: string;
  };
  CHAT_NOTIFICATION: {
    id: string;
  };
  JOB_POSTS_NOTIFICATION: Record<never, never>;
  CHAT_NOTIFICATION_ORG: {
    id: string;
    organizationId?: string;
    organizationBusinessUnitId: string;
  };
  JOB_POST_INTEREST: {
    jobPostId: string;
    organizationId?: string;
    organizationBusinessUnitId?: string;
  };
  TIME_ENTRY_REMINDER: Record<never, never>;
  TIMESHEET_UPDATE: Record<never, never>;
  JOB_OFFER_EDIT_PROPOSAL_ACCEPTED: {
    jobOfferId: string;
  };
  JOB_OFFER_EDIT_PROPOSAL_ACCEPTED_ORG: {
    jobOfferId: string;
    organizationId?: string;
    organizationBusinessUnitId: string;
  };
  JOB_OFFER_ACCEPTED_ORG: {
    jobOfferId: string;
    organizationId?: string;
    organizationBusinessUnitId: string;
  };
  JOB_OFFER_REJECTED_ORG: {
    jobOfferId: string;
    organizationId?: string;
    organizationBusinessUnitId: string;
    reason?: string;
  };
  JOB_OFFER_NEW: {
    jobOfferId: string;
  };
  HIRING_PROCESS_PENDING: Record<never, never>;
  FORM_ASSIGNMENT_COMPLETED_ORG: {
    formAssignmentId: string;
    organizationBusinessUnitId: string;
    chatThreadId?: string;
  };
  FORM_ASSIGNMENT_REJECTED_ORG: {
    formAssignmentId: string;
    organizationBusinessUnitId: string;
    chatThreadId?: string;
  };
  FORM_ASSIGNMENT_RECEIVED: {
    formAssignmentId: string;
    chatThreadId?: string;
  };
  TIMESHEET_SUBMITTED_ORG: {
    organizationId?: string;
    organizationBusinessUnitId: string;
  };
};

const navigateToOrgNotification = ({
  to,
  search,
  organizationId,
  organizationBusinessUnitId,
}: {
  to: string;
  search?: Record<string, string>;
  organizationId?: string;
  organizationBusinessUnitId?: string;
}) => {
  void (async () => {
    if (organizationId) {
      await Storage.setItem("selectedOrgId", organizationId);
    }
    if (organizationBusinessUnitId) {
      await Storage.setItem(
        "selectedOrgBusinessUnitId",
        organizationBusinessUnitId,
      );
    }

    await router.navigate({
      to,
      search,
      reloadDocument: true,
    });
  })();
};

const routerHandler = {
  CREDENTIAL_EXPIRATION_REMINDER: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["CREDENTIAL_EXPIRATION_REMINDER"],
  ) => {
    router.navigate({
      to: `/provider/documents`,
      search: {
        fileId: data.id,
      },
    });
  },
  CHAT_NOTIFICATION: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["CHAT_NOTIFICATION"],
  ) => {
    router.navigate({
      to: `/provider/chat`,
      search: {
        thread: data.id,
      },
    });
  },
  CHAT_NOTIFICATION_ORG: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["CHAT_NOTIFICATION_ORG"],
  ) => {
    const { organizationId, organizationBusinessUnitId, id } = data;
    navigateToOrgNotification({
      to: `/org/bu/${organizationBusinessUnitId}/chat`,
      search: { thread: id },
      organizationId,
      organizationBusinessUnitId,
    });
  },
  JOB_POSTS_NOTIFICATION: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["JOB_POSTS_NOTIFICATION"],
  ) => {
    console.log("JOB_POSTS_NOTIFICATION", data);
    router.navigate({
      to: `/provider/jobs/recommended`,
    });
  },
  JOB_POST_INTEREST: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["JOB_POST_INTEREST"],
  ) => {
    navigateToOrgNotification({
      to: data.organizationBusinessUnitId
        ? `/org/bu/${data.organizationBusinessUnitId}/jobs/post/dashboard/${data.jobPostId}`
        : data.organizationId
          ? `/org/${data.organizationId}/jobs/post/dashboard/${data.jobPostId}`
          : `/org/jobs/post/dashboard/${data.jobPostId}`,
      organizationId: data.organizationId,
      organizationBusinessUnitId: data.organizationBusinessUnitId,
    });
  },
  TIME_ENTRY_REMINDER: (
    _data: PUSH_NOTIFICATION_ACTION_TYPE["TIME_ENTRY_REMINDER"],
  ) => {
    router.navigate({
      to: `/provider/jobs/timesheets`,
    });
  },
  TIMESHEET_UPDATE: (
    _data: PUSH_NOTIFICATION_ACTION_TYPE["TIMESHEET_UPDATE"],
  ) => {
    router.navigate({
      to: `/provider/jobs/timesheets`,
    });
  },
  JOB_OFFER_EDIT_PROPOSAL_ACCEPTED: (
    _data: PUSH_NOTIFICATION_ACTION_TYPE["JOB_OFFER_EDIT_PROPOSAL_ACCEPTED"],
  ) => {
    router.navigate({
      to: `/provider/jobs/offers`,
    });
  },
  JOB_OFFER_EDIT_PROPOSAL_ACCEPTED_ORG: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["JOB_OFFER_EDIT_PROPOSAL_ACCEPTED_ORG"],
  ) => {
    const { organizationId, organizationBusinessUnitId, jobOfferId } = data;
    navigateToOrgNotification({
      to: `/org/bu/${organizationBusinessUnitId}/jobs/offer/edit/${jobOfferId}`,
      organizationId,
      organizationBusinessUnitId,
    });
  },
  JOB_OFFER_ACCEPTED_ORG: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["JOB_OFFER_ACCEPTED_ORG"],
  ) => {
    const { organizationId, organizationBusinessUnitId, jobOfferId } = data;
    navigateToOrgNotification({
      to: `/org/bu/${organizationBusinessUnitId}/jobs/offer`,
      search: { offerId: jobOfferId },
      organizationId,
      organizationBusinessUnitId,
    });
  },
  JOB_OFFER_REJECTED_ORG: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["JOB_OFFER_REJECTED_ORG"],
  ) => {
    const { organizationId, organizationBusinessUnitId, jobOfferId } = data;
    navigateToOrgNotification({
      to: `/org/bu/${organizationBusinessUnitId}/jobs/offer`,
      search: { offerId: jobOfferId },
      organizationId,
      organizationBusinessUnitId,
    });
  },
  JOB_OFFER_NEW: (_data: PUSH_NOTIFICATION_ACTION_TYPE["JOB_OFFER_NEW"]) => {
    router.navigate({
      to: `/provider/jobs/active`,
    });
  },
  HIRING_PROCESS_PENDING: (
    _data: PUSH_NOTIFICATION_ACTION_TYPE["HIRING_PROCESS_PENDING"],
  ) => {
    router.navigate({
      to: `/provider/home`,
    });
  },
  FORM_ASSIGNMENT_COMPLETED_ORG: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["FORM_ASSIGNMENT_COMPLETED_ORG"],
  ) => {
    const { organizationBusinessUnitId, chatThreadId } = data;
    navigateToOrgNotification({
      to: `/org/bu/${organizationBusinessUnitId}/chat`,
      search: chatThreadId ? { thread: chatThreadId } : undefined,
      organizationBusinessUnitId,
    });
  },
  FORM_ASSIGNMENT_REJECTED_ORG: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["FORM_ASSIGNMENT_REJECTED_ORG"],
  ) => {
    const { organizationBusinessUnitId, chatThreadId } = data;
    navigateToOrgNotification({
      to: `/org/bu/${organizationBusinessUnitId}/chat`,
      search: chatThreadId ? { thread: chatThreadId } : undefined,
      organizationBusinessUnitId,
    });
  },
  FORM_ASSIGNMENT_RECEIVED: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["FORM_ASSIGNMENT_RECEIVED"],
  ) => {
    router.navigate({
      to: `/provider/chat`,
      search: data.chatThreadId ? { thread: data.chatThreadId } : undefined,
    });
  },
  TIMESHEET_SUBMITTED_ORG: (
    data: PUSH_NOTIFICATION_ACTION_TYPE["TIMESHEET_SUBMITTED_ORG"],
  ) => {
    const { organizationId, organizationBusinessUnitId } = data;
    navigateToOrgNotification({
      to: `/org/bu/${organizationBusinessUnitId}/jobs/timesheets`,
      search: { tab: "pending" },
      organizationId,
      organizationBusinessUnitId,
    });
  },
};

export const handlePushNotificationActions = ({
  action,
  data,
}: {
  action: keyof PUSH_NOTIFICATION_ACTION_TYPE;
  data: PUSH_NOTIFICATION_ACTION_TYPE[keyof PUSH_NOTIFICATION_ACTION_TYPE];
}) => {
  const handler = routerHandler[action] as (
    data: PUSH_NOTIFICATION_ACTION_TYPE[keyof PUSH_NOTIFICATION_ACTION_TYPE],
  ) => void;
  if (handler) {
    handler(data);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};
